import {
  formatMoney,
  getPaymentWorkflowOptionsDisabledForDataMigrationStatus,
  isNullish,
  noOp,
  PaymentWorkflowOption,
  PaymentWorkflowOptionSchema,
  usCentsToUsd,
} from '@breezy/shared'
import { faPiggyBank } from '@fortawesome/pro-light-svg-icons'
import { faInfoCircle } from '@fortawesome/pro-regular-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { zodResolver } from '@hookform/resolvers/zod'
import { Form, Switch, Tooltip } from 'antd'
import classNames from 'classnames'
import React, { useMemo } from 'react'
import { useForm } from 'react-hook-form'
import { z } from 'zod'
import { NumberField } from '../../../../elements/Forms/NumberField'
import { ReactHookFormItem } from '../../../../elements/Forms/ReactHookFormItem'
import RenderIf from '../../../../elements/RenderIf/RenderIf'
import ThinDivider from '../../../../elements/ThinDivider'
import { useCompanyDataMigrationStatus } from '../../../../providers/PrincipalUser'
import { PAYMENT_WORKFLOW_OPTIONS, PaymentWorkflowOptionConfig } from '../../PaymentWorkflowWizard'

export const PaymentWorkflowConfigFormSchema = z
  .object({
    paymentAmountUsd: z.number().min(0.01, 'Payment amount must be greater than $0'),
    paymentWorkflowOption: PaymentWorkflowOptionSchema,
    hiddenAmountUsd: z.number(),
    shouldSendReceipt: z.boolean().default(true),
  })
  .superRefine((data, ctx) => {
    // Check for minimum amount
    if (!data.paymentAmountUsd || data.paymentAmountUsd <= 0) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['paymentAmountUsd'],
        message: 'Payment amount must be greater than $0',
      })
    }
    // Check for maximum amount
    if (data.paymentAmountUsd && data.paymentAmountUsd > data.hiddenAmountUsd) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ['paymentAmountUsd'],
        message: `Payment amount must be less than or equal to the invoice amount of ${formatMoney(
          data.hiddenAmountUsd,
        )}`,
      })
    }
  })

export type PaymentWorkflowConfigFormData = z.infer<typeof PaymentWorkflowConfigFormSchema>

export type PaymentWorkflowConfigSubmitFormData = {
  paymentAmountUsd: number
  paymentWorkflowOption: PaymentWorkflowOption
  shouldSendReceipt?: boolean
}

export type PaymentWorkflowConfigFormProps = PaymentWorkflowOptionConfig & {
  amountUsd: number
  onSubmit: (data: PaymentWorkflowConfigSubmitFormData) => void
  editableAmount?: boolean
  defaultPaymentAmountUsc?: number
  paymentAmountLabel?: string
  onCollectLater?: () => void
}

const PaymentWorkflowConfigForm = ({
  onSubmit,
  defaultPaymentAmountUsc,
  amountUsd,
  paymentInfo,
  editableAmount = true,
  paymentAmountLabel = 'Enter a payment amount',
  onCollectLater,
  ...props
}: PaymentWorkflowConfigFormProps) => {
  const dataMigrationStatus = useCompanyDataMigrationStatus()
  const dataMigrationDisabledOptions = useMemo(
    () => getPaymentWorkflowOptionsDisabledForDataMigrationStatus(dataMigrationStatus),
    [dataMigrationStatus],
  )

  const {
    control,
    formState: { errors },
    watch,
    getValues,
    trigger,
  } = useForm<PaymentWorkflowConfigFormData>({
    defaultValues: {
      paymentAmountUsd: usCentsToUsd(defaultPaymentAmountUsc ?? 0),
      paymentWorkflowOption: 'Payment Link' as PaymentWorkflowOption,
      hiddenAmountUsd: amountUsd,
      shouldSendReceipt: true,
    },
    mode: 'onChange',
    reValidateMode: 'onChange',
    resolver: zodResolver(PaymentWorkflowConfigFormSchema),
  })

  const visibleOptions = PAYMENT_WORKFLOW_OPTIONS.filter(
    option => !props.hiddenOptions?.includes(option.label as PaymentWorkflowOption),
  )

  const allOptionsDisabled = amountUsd <= 0

  const disabledOptions = useMemo(() => {
    if (allOptionsDisabled) {
      return visibleOptions.map(option => ({
        label: option.label as PaymentWorkflowOption,
        info: 'No payment due',
        hide: false,
      }))
    }

    const existingDisabled =
      props.disabledOptions?.map(option => ({
        label: option.option,
        info: option.info,
        hide: option.hide,
      })) ?? []

    if (dataMigrationDisabledOptions.length > 0) {
      const disabledLabels = new Set(existingDisabled.map(option => option.label))
      dataMigrationDisabledOptions.forEach(option => {
        if (!disabledLabels.has(option)) {
          existingDisabled.push({
            label: option,
            info: 'Unavailable until data migration is complete',
            hide: false,
          })
          disabledLabels.add(option)
        }
      })
    }

    return existingDisabled
  }, [allOptionsDisabled, dataMigrationDisabledOptions, props.disabledOptions, visibleOptions])

  const disabledOptionLabels = useMemo(() => new Set(disabledOptions.map(option => option.label)), [disabledOptions])

  const enabledOptions = allOptionsDisabled
    ? []
    : visibleOptions.filter(option => !disabledOptionLabels.has(option.label as PaymentWorkflowOption))

  const formPaymentAmountUsd = watch('paymentAmountUsd')

  const onSubmitInner = async (paymentOption: PaymentWorkflowOption) => {
    // Get fresh values to avoid stale closure issues
    const currentValues = getValues()
    const validationResult = await trigger('paymentAmountUsd')

    // Don't proceed if validation failed
    if (!validationResult) {
      return
    }

    onSubmit({
      paymentAmountUsd: currentValues.paymentAmountUsd,
      paymentWorkflowOption: paymentOption,
      shouldSendReceipt: currentValues.shouldSendReceipt,
    })
  }

  return (
    <Form layout="vertical">
      {
        <RenderIf if={!isNullish(defaultPaymentAmountUsc)}>
          <ReactHookFormItem
            control={control}
            name="paymentAmountUsd"
            label={paymentAmountLabel}
            errors={
              formPaymentAmountUsd > amountUsd
                ? {
                    paymentAmountUsd: { type: 'custom', message: 'Over limit' },
                  }
                : !formPaymentAmountUsd || formPaymentAmountUsd <= 0
                  ? { paymentAmountUsd: { type: 'custom', message: 'Too low' } }
                  : {}
            }
            hideErrors={true} // Hide the default error display
            render={({ field }) => (
              <NumberField
                className="w-full"
                dataDdActionName="BZ Payment Workflow - Set Payment Amount"
                isMoney
                disabled={!editableAmount}
                {...field}
              />
            )}
          />
          {/* Display error message directly based on current values */}
          {formPaymentAmountUsd > amountUsd && (
            <div className="mt-1 text-sm text-red-500">
              Payment amount must be less than or equal to the invoice amount of {formatMoney(amountUsd)}
            </div>
          )}
          {(!formPaymentAmountUsd || formPaymentAmountUsd <= 0) && (
            <div className="mt-1 text-sm text-red-500">Payment amount must be greater than $0</div>
          )}
          {paymentInfo}

          <ReactHookFormItem
            control={control}
            name="shouldSendReceipt"
            label={
              <div className="flex flex-row items-center gap-2">
                <span>Send Payment Receipt</span>
                <Tooltip title="Disable to prevent sending a receipt email to the customer">
                  <FontAwesomeIcon icon={faInfoCircle} />
                </Tooltip>
              </div>
            }
            errors={errors}
            render={({ field }) => (
              <div className="mb-6">
                <Switch
                  checked={field.value}
                  onChange={field.onChange}
                  data-dd-action-name="BZ Payment Workflow - Toggle Receipt"
                />
              </div>
            )}
          />

          <ThinDivider
            dividerStyle="dotted"
            styleOverrides={{
              marginTop: 24,
              marginBottom: 24,
            }}
          />

          <ReactHookFormItem
            control={control}
            name="paymentWorkflowOption"
            label="Choose a payment option"
            errors={errors}
            render={({ field }) => (
              <div className="grid grid-cols-2 gap-3 md:grid-cols-3">
                {enabledOptions.map(option => {
                  // Check if payment amount is invalid (too low or too high)
                  const isInvalidAmount =
                    !formPaymentAmountUsd || formPaymentAmountUsd <= 0 || formPaymentAmountUsd > amountUsd
                  return (
                    <PaymentWorkflowOptionCard
                      key={option.label}
                      option={option.label}
                      status={isInvalidAmount ? 'disabled' : 'default'}
                      onClick={() => onSubmitInner(option.label)}
                    />
                  )
                })}
                {disabledOptions.map(option => (
                  <React.Fragment key={option.label}>
                    {!option.hide && (
                      <PaymentWorkflowOptionCard
                        key={option.label}
                        option={option.label}
                        info={option.info}
                        status="disabled"
                        onClick={noOp}
                      />
                    )}
                  </React.Fragment>
                ))}
              </div>
            )}
          />
        </RenderIf>
      }
    </Form>
  )
}

type PaymentWorkflowOptionCardProps = {
  option: PaymentWorkflowOption
  status: 'default' | 'disabled'
  info?: string
  onClick: () => void
}

const PaymentWorkflowOptionCard = ({ option, status, info, onClick }: PaymentWorkflowOptionCardProps) => {
  const optionConfig = PAYMENT_WORKFLOW_OPTIONS.find(config => config.label === option) ?? {
    icon: faPiggyBank,
  }
  return (
    <div
      onClick={onClick}
      data-testid={`paymentOptionCard-${option}`}
      data-dd-action-name={`BZ Payment Workflow - Select Option - ${option}`}
      className={classNames(
        'relative flex flex-col items-center justify-center gap-2 rounded-lg border border-solid border-bz-gray-500 px-4 py-6 font-semibold',
        {
          'cursor-pointer text-bz-gray-1000': status === 'default',
          'cursor-not-allowed bg-bz-gray-400 text-bz-gray-600': status === 'disabled',
        },
      )}
    >
      <RenderIf if={!!info}>
        <div className="absolute right-1 top-0">
          <Tooltip title={info} placement="top">
            <FontAwesomeIcon icon={faInfoCircle} />
          </Tooltip>
        </div>
      </RenderIf>
      <FontAwesomeIcon
        className={classNames('h-8 w-8', {
          'text-bz-gray-1000': status === 'default',
          'text-bz-gray-600': status === 'disabled',
        })}
        icon={optionConfig.icon}
      />
      <div>{option}</div>
    </div>
  )
}

export default PaymentWorkflowConfigForm
